// snake_case proposital: espelha o serializer da API, não renomear pra camelCase
export type BulkImportStatus =
  | 'uploaded'
  | 'validating'
  | 'validated'
  | 'invalid'
  | 'ready'
  | 'processing'
  | 'completed'
  | 'failed'

export type BulkImportEntity = 'users' | 'vehicles' | 'cards'

export type BulkImportTemplateEntity = Exclude<BulkImportEntity, 'cards'>

export type BulkImportMode = 'create' | 'edit'

export type BulkImportStep =
  | 'selector'
  | 'upload'
  | 'validating'
  | 'summary'
  | 'validation_error'
  | 'processing'
  | 'result_success'
  | 'result_partial'

export interface BulkImportTotals {
  total: number
  valid: number
  blocked: number
  ignored: number
  succeeded: number
  failed: number
  with_card?: number
  without_card?: number
  new_cards?: number
}

export interface BulkImportProgress {
  processed_count: number
  total_count: number
  percent: number
}

export interface BulkImportCreatedBy {
  id: string
  name: string
}

export interface BulkImport {
  id: string
  operation_type: string
  entity: BulkImportEntity
  status: BulkImportStatus
  file_name: string | null
  totals: BulkImportTotals
  progress: BulkImportProgress
  has_error_report: boolean
  has_result_report: boolean
  cards_failed: number
  has_card_failures: boolean
  error_message: string | null
  created_by: BulkImportCreatedBy | null
  created_at: string
  finished_at: string | null
}

export interface BulkExportListItem {
  id: string
  entity: BulkImportEntity
  operation_type: string
  row_count: number
  author_name: string | null
  created_at: string
}

export type BulkCentralSortDirection = 'asc' | 'desc'

// Valores aceitos por order_by em GET /manager/imports (default do back: created_at desc).
export type BulkImportCentralSortField = 'created_at' | 'author' | 'entity' | 'status'

// Valores aceitos por order_by em GET /manager/bulk_exports (default do back: created_at desc).
export type BulkExportSortField = 'created_at' | 'author' | 'entity' | 'row_count'

export type BulkImportErrorStatus = 'error' | 'warning' | 'ignored'

export interface BulkImportErrorRow {
  line: number
  identifier: string
  status: BulkImportErrorStatus
  messages: string[]
  fields?: string[]
  values?: string[]
}

export function mapBulkImportFromApi(raw: unknown): BulkImport {
  const obj = raw as Record<string, unknown>

  const totals = obj.totals as Record<string, unknown> | undefined
  const progress = obj.progress as Record<string, unknown> | undefined

  return {
    id: String(obj.id ?? ''),
    operation_type: String(obj.operation_type ?? ''),
    entity: (obj.entity ?? '') as BulkImportEntity,
    status: (obj.status ?? '') as BulkImportStatus,
    file_name: obj.file_name != null ? String(obj.file_name) : null,
    totals: {
      total: typeof totals?.total === 'number' ? totals.total : 0,
      valid: typeof totals?.valid === 'number' ? totals.valid : 0,
      blocked: typeof totals?.blocked === 'number' ? totals.blocked : 0,
      ignored: typeof totals?.ignored === 'number' ? totals.ignored : 0,
      succeeded: typeof totals?.succeeded === 'number' ? totals.succeeded : 0,
      failed: typeof totals?.failed === 'number' ? totals.failed : 0,
      with_card: typeof totals?.with_card === 'number' ? totals.with_card : undefined,
      without_card: typeof totals?.without_card === 'number' ? totals.without_card : undefined,
      new_cards: typeof totals?.new_cards === 'number' ? totals.new_cards : undefined,
    },
    progress: {
      processed_count: typeof progress?.processed_count === 'number' ? progress.processed_count : 0,
      total_count: typeof progress?.total_count === 'number' ? progress.total_count : 0,
      percent: typeof progress?.percent === 'number' ? progress.percent : 0,
    },
    has_error_report: Boolean(obj.has_error_report),
    has_result_report: Boolean(obj.has_result_report),
    cards_failed: typeof obj.cards_failed === 'number' ? obj.cards_failed : 0,
    has_card_failures: Boolean(obj.has_card_failures),
    error_message: obj.error_message != null ? String(obj.error_message) : null,
    created_by:
      obj.created_by && typeof obj.created_by === 'object'
        ? {
            id: String((obj.created_by as Record<string, unknown>).id ?? ''),
            name: String((obj.created_by as Record<string, unknown>).name ?? ''),
          }
        : null,
    created_at: String(obj.created_at ?? ''),
    finished_at: obj.finished_at != null ? String(obj.finished_at) : null,
  }
}

export function mapBulkExportFromApi(raw: unknown): BulkExportListItem {
  const obj = raw as Record<string, unknown>

  return {
    id: String(obj.id ?? ''),
    entity: (obj.entity ?? '') as BulkImportEntity,
    operation_type: String(obj.operation_type ?? 'edit'),
    row_count: typeof obj.row_count === 'number' ? obj.row_count : 0,
    author_name: obj.author_name != null ? String(obj.author_name) : null,
    created_at: String(obj.created_at ?? ''),
  }
}

const ERROR_ROW_STATUS_MAP: Record<string, BulkImportErrorStatus> = {
  blocked: 'error',
  failed: 'error',
  error: 'error',
  warning: 'warning',
  ignored: 'ignored',
}

function mapErrorRowStatus(raw: unknown): BulkImportErrorStatus {
  return ERROR_ROW_STATUS_MAP[String(raw)] ?? 'error'
}

export function mapBulkImportErrorRowFromApi(raw: unknown): BulkImportErrorRow {
  const obj = raw as Record<string, unknown>
  const messages = obj.messages
  const fields = obj.fields
  const values = obj.values

  return {
    line: typeof obj.line === 'number' ? obj.line : 0,
    identifier: String(obj.identifier ?? ''),
    status: mapErrorRowStatus(obj.status),
    messages:
      Array.isArray(messages) && messages.every((m) => typeof m === 'string')
        ? messages
        : [],
    fields: Array.isArray(fields) ? fields.map((field) => String(field ?? '')) : undefined,
    values: Array.isArray(values) ? values.map((value) => (value == null ? '' : String(value))) : undefined,
  }
}
